/**
 * LayoutRenderer — the only renderer in the system.
 *
 * Both the public viewer page and the Director preview pane mount this
 * component with a {@link Layout}. It validates, picks a shell, fills
 * the shell's named slots with primitive instances from the layout JSON,
 * and hands the lot to the shell to arrange visually.
 *
 *   <LayoutRenderer
 *     layout={event.layout ?? TEMPLATE_REGISTRY[event.template_key].defaultLayout}
 *     event={...}
 *     cameras={...}
 *     room={...}
 *   />
 *
 * Shells live in event-viewer/shells/ and are registered in SHELL_REGISTRY.
 * Primitives live in event-viewer/primitives/ and are registered via
 * {@link registerPrimitive}.
 */

import {
  Component,
  Fragment,
  type ErrorInfo,
  type ReactNode,
} from 'react'
import { Notice } from '@/components/play'
import { lookupPrimitive } from './PrimitiveRegistry'
import { lookupShell, type Shell, type ShellSlotMap } from './ShellRegistry'
import { UnknownPrimitive } from './UnknownPrimitive'
import { LayoutProvider, type LayoutContextValue } from './LayoutContext'
import { validateLayout } from './schema'
import type { Layout, PrimitiveInstance } from './types'

export interface LayoutRendererProps {
  layout: Layout
  /** Everything primitives need at render time. Becomes context. */
  context: LayoutContextValue
}

interface ComponentRenderBoundaryProps {
  instanceId: string
  primitiveName: string
  resetToken: string
  children: ReactNode
}

interface ComponentRenderBoundaryState {
  failed: boolean
}

/**
 * Isolates destination component failures so one provider or component cannot
 * blank the whole event. A later config/publication revision retries the same
 * stable instance without requiring the viewer to reload.
 */
export class ComponentRenderBoundary extends Component<
  ComponentRenderBoundaryProps,
  ComponentRenderBoundaryState
> {
  state: ComponentRenderBoundaryState = { failed: false }

  static getDerivedStateFromError(): ComponentRenderBoundaryState {
    return { failed: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[destination] component render failed', {
      component: this.props.primitiveName,
      instanceId: this.props.instanceId,
      error,
      componentStack: info.componentStack,
    })
  }

  componentDidUpdate(previous: ComponentRenderBoundaryProps) {
    if (
      this.state.failed &&
      previous.resetToken !== this.props.resetToken
    ) {
      this.setState({ failed: false })
    }
  }

  render() {
    if (!this.state.failed) return this.props.children
    if (import.meta.env.PROD) return null

    return (
      <Notice tone="error" aria-label={`${this.props.primitiveName} failed to render`}>
        Component unavailable: <span className="font-mono">{this.props.primitiveName}</span>
      </Notice>
    )
  }
}

function renderInstance(instance: PrimitiveInstance): ReactNode {
  const Component = lookupPrimitive(instance.primitive)
  if (!Component) {
    return (
      <UnknownPrimitive
        key={instance.id}
        primitiveName={instance.primitive}
        instanceId={instance.id}
      />
    )
  }
  const resetToken = `${instance.primitive}:${JSON.stringify(instance.config)}`
  return (
    <ComponentRenderBoundary
      key={instance.id}
      instanceId={instance.id}
      primitiveName={instance.primitive}
      resetToken={resetToken}
    >
      <Component id={instance.id} config={instance.config} />
    </ComponentRenderBoundary>
  )
}

function renderSlots(layout: Layout): ShellSlotMap {
  const out: ShellSlotMap = {}
  for (const [slotName, instances] of Object.entries(layout.slots)) {
    out[slotName] = (
      <Fragment>
        {instances.map((instance) => renderInstance(instance))}
      </Fragment>
    )
  }
  return out
}

export function LayoutRenderer({ layout, context }: LayoutRendererProps) {
  // Re-validate even though Rails already did — defends against forked layouts
  // that survived a schema change. On invalid, fall back to a minimal error
  // state rather than rendering garbage.
  const result = validateLayout(layout)
  if (!result.ok) {
    if (import.meta.env.PROD) {
      return null
    }
    return (
      <div className="p-6">
        <Notice tone="warning" title="Invalid layout">
          <ul className="font-mono text-xs">
            {result.errors.map((e, i) => (
              <li key={i}>
                {e.path}: {e.message}
              </li>
            ))}
          </ul>
        </Notice>
      </div>
    )
  }

  const validated = result.value

  const ShellComponent: Shell | undefined = lookupShell(validated.shell)
  if (!ShellComponent) {
    if (import.meta.env.PROD) return null
    return (
      <div className="p-6">
        <Notice tone="warning" title="Unknown shell">
          <p className="font-mono">{validated.shell}</p>
        </Notice>
      </div>
    )
  }

  const slots = renderSlots(validated)

  return (
    <LayoutProvider value={context}>
      <ShellComponent slots={slots} />
    </LayoutProvider>
  )
}
